import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Mail, Send, RefreshCw, Eye, Calendar, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface TemplateInfo {
  name: string;
  label: string;
  sample_vars: Record<string, any>;
}

interface ProviderOption {
  id: string;
  name: string;
}

interface BookingOption {
  id: string;
  booking_number: string;
  booking_date: string;
  start_time: string;
  customer_name: string;
  provider_name: string;
  service_name: string;
}

interface PreviewData {
  subject: string;
  html: string;
  text: string;
  vars: Record<string, any>;
  source: "sample" | "real_booking" | "provider_sample";
  booking_id?: string | null;
}

const ANY_PROVIDER = "__any__";

const sourceLabel = (s: PreviewData["source"]) => {
  switch (s) {
    case "real_booking": return "Real booking data";
    case "provider_sample": return "Provider branding + sample data";
    default: return "Sample data";
  }
};

const EmailTemplatesManagement = () => {
  const { toast } = useToast();
  const [templates, setTemplates] = useState<TemplateInfo[]>([]);
  const [providers, setProviders] = useState<ProviderOption[]>([]);
  const [selected, setSelected] = useState<string>("booking-confirmed");
  const [providerId, setProviderId] = useState<string>(ANY_PROVIDER);
  const [bookingId, setBookingId] = useState<string | undefined>(undefined);
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [loading, setLoading] = useState(false);
  const [recipient, setRecipient] = useState("andynrx@gmail.com");
  const [sending, setSending] = useState(false);

  // Booking picker state
  const [pickerOpen, setPickerOpen] = useState(false);
  const [bookings, setBookings] = useState<BookingOption[]>([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [bookingSearch, setBookingSearch] = useState("");

  // Load template list + providers
  useEffect(() => {
    (async () => {
      const [tplRes, provRes] = await Promise.all([
        supabase.functions.invoke("admin-email-template-preview", { body: { action: "list" } }),
        supabase.functions.invoke("admin-email-template-preview", { body: { action: "list-providers" } }),
      ]);
      if (tplRes.error) {
        toast({ title: "Failed to load templates", description: tplRes.error.message, variant: "destructive" });
      } else {
        setTemplates(tplRes.data?.templates || []);
      }
      if (provRes.error) {
        toast({ title: "Failed to load providers", description: provRes.error.message, variant: "destructive" });
      } else {
        setProviders(provRes.data?.providers || []);
      }
    })();
  }, [toast]);

  const loadPreview = useCallback(async (template: string, pid: string, bid?: string) => {
    setLoading(true);
    setPreview(null);
    const { data, error } = await supabase.functions.invoke(
      "admin-email-template-preview",
      {
        body: {
          action: "preview",
          template,
          provider_id: pid === ANY_PROVIDER ? undefined : pid,
          booking_id: bid,
        },
      }
    );
    setLoading(false);
    if (error) {
      toast({ title: "Preview failed", description: error.message, variant: "destructive" });
      return;
    }
    setPreview(data);
  }, [toast]);

  // Reload preview when template / provider / bookingId changes
  useEffect(() => {
    if (!selected) return;
    loadPreview(selected, providerId, bookingId);
  }, [selected, providerId, bookingId, loadPreview]);

  const openPicker = async () => {
    setPickerOpen(true);
    if (bookings.length > 0) return;
    setBookingsLoading(true);
    const { data, error } = await supabase.functions.invoke(
      "admin-email-template-preview",
      { body: { action: "list-bookings" } }
    );
    setBookingsLoading(false);
    if (error) {
      toast({ title: "Failed to load bookings", description: error.message, variant: "destructive" });
      return;
    }
    setBookings(data?.bookings || []);
  };

  const selectBooking = (b: BookingOption) => {
    setBookingId(b.id);
    setPickerOpen(false);
  };

  const resetBooking = () => {
    setBookingId(undefined);
  };

  const sendTest = async () => {
    if (!recipient) return;
    setSending(true);
    const { data, error } = await supabase.functions.invoke(
      "admin-email-template-preview",
      {
        body: {
          action: "test-send",
          template: selected,
          recipient,
          provider_id: providerId === ANY_PROVIDER ? undefined : providerId,
          booking_id: bookingId,
        },
      }
    );
    setSending(false);
    if (error || !data?.success) {
      toast({
        title: "Test send failed",
        description: error?.message || data?.result?.error || "Unknown error",
        variant: "destructive",
      });
      return;
    }
    toast({
      title: "Test email sent",
      description: `Sent to ${recipient} using ${sourceLabel(data.source)}.`,
    });
  };

  const selectedProviderName =
    providerId === ANY_PROVIDER
      ? "Most recent booking (any provider)"
      : providers.find((p) => p.id === providerId)?.name;

  const activeBooking = bookings.find((b) => b.id === bookingId);
  const activeBookingNumber =
    activeBooking?.booking_number ||
    (bookingId && preview?.vars?.booking_number) ||
    (preview?.source === "real_booking" ? preview.vars?.booking_number : undefined);

  const filteredBookings = bookings.filter((b) => {
    if (!bookingSearch.trim()) return true;
    const q = bookingSearch.toLowerCase();
    return (
      b.booking_number?.toLowerCase().includes(q) ||
      b.customer_name?.toLowerCase().includes(q) ||
      b.provider_name?.toLowerCase().includes(q) ||
      b.service_name?.toLowerCase().includes(q)
    );
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Email Templates
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Controls */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
          <div className="md:col-span-3 space-y-2">
            <Label>Template</Label>
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {templates.map((t) => (
                  <SelectItem key={t.name} value={t.name}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3 space-y-2">
            <Label>Preview as provider</Label>
            <Select value={providerId} onValueChange={(v) => { setProviderId(v); setBookingId(undefined); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY_PROVIDER}>Any (most recent booking)</SelectItem>
                {providers.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3 space-y-2">
            <Label>Send test to</Label>
            <Input
              type="email"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder="email@example.com"
            />
          </div>
          <div className="md:col-span-3 flex gap-2">
            <Button variant="outline" onClick={() => loadPreview(selected, providerId, bookingId)} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button onClick={sendTest} disabled={sending || !recipient}>
              <Send className="h-4 w-4 mr-2" />
              {sending ? "Sending..." : "Send Test"}
            </Button>
          </div>
        </div>

        {/* Booking picker controls */}
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={openPicker}>
            <Calendar className="h-4 w-4 mr-2" />
            Use different booking example
          </Button>
          {bookingId && (
            <>
              <Badge variant="secondary" className="gap-1">
                Booking: {activeBookingNumber || bookingId.slice(0, 8)}
              </Badge>
              <Button variant="ghost" size="sm" onClick={resetBooking}>
                <X className="h-4 w-4 mr-1" />
                Reset to sample
              </Button>
            </>
          )}
        </div>

        {/* Preview area */}
        {preview && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="gap-1">
                <Eye className="h-3 w-3" /> Preview
              </Badge>
              <Badge variant={preview.source === "real_booking" ? "default" : "secondary"}>
                {sourceLabel(preview.source)}
              </Badge>
              {selectedProviderName && (
                <Badge variant="outline">{selectedProviderName}</Badge>
              )}
              <span className="text-sm text-muted-foreground ml-auto">
                Subject: <strong className="text-foreground">{preview.subject}</strong>
              </span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 border rounded-lg overflow-hidden bg-white">
                <iframe
                  title="Email preview"
                  srcDoc={preview.html}
                  className="w-full"
                  style={{ height: "720px", border: 0 }}
                />
              </div>

              <div className="space-y-3">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Variables used</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <pre className="text-xs bg-muted p-3 rounded overflow-auto max-h-72">
                      {JSON.stringify(preview.vars, null, 2)}
                    </pre>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Plain text version</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <pre className="text-xs bg-muted p-3 rounded overflow-auto max-h-72 whitespace-pre-wrap">
                      {preview.text}
                    </pre>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        )}

        {loading && !preview && (
          <div className="py-12 text-center text-muted-foreground">Loading preview...</div>
        )}

        {/* Booking picker dialog */}
        <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Choose a booking example</DialogTitle>
              <DialogDescription>
                Select one of the most recent bookings to use as preview data.
              </DialogDescription>
            </DialogHeader>
            <Input
              placeholder="Search by number, customer, provider, service..."
              value={bookingSearch}
              onChange={(e) => setBookingSearch(e.target.value)}
            />
            <div className="max-h-96 overflow-auto border rounded-md divide-y">
              {bookingsLoading ? (
                <div className="p-6 text-center text-sm text-muted-foreground">Loading bookings...</div>
              ) : filteredBookings.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">No bookings found.</div>
              ) : (
                filteredBookings.map((b) => (
                  <button
                    key={b.id}
                    onClick={() => selectBooking(b)}
                    className={`w-full text-left p-3 hover:bg-muted transition-colors ${
                      b.id === bookingId ? "bg-muted" : ""
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium text-sm">{b.booking_number}</div>
                      <div className="text-xs text-muted-foreground">
                        {b.booking_date} · {b.start_time?.slice(0, 5)}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {b.customer_name} — {b.service_name} @ {b.provider_name}
                    </div>
                  </button>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default EmailTemplatesManagement;
